import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { DialogContent } from '../../../components/ui/DialogContent';
import { DialogActions } from '../../../components/ui/DialogActions';
import { useMemo, useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import {
  collectionLeaveErrorMessage,
  createCollectionLeaveController,
} from '../collectionMembershipService';

export interface CollectionLeaveSectionProps {
  collectionId: string;
  collectionName?: string;
  onCancel?: () => void;
  userId: string;
  role: 'administrator' | 'editor' | 'viewer';
  onLeft(): Promise<void> | void;
}

export function CollectionLeaveSection({
  collectionId,
  collectionName = 'Active collection',
  onCancel,
  userId,
  role,
  onLeft,
}: CollectionLeaveSectionProps) {
  const controller = useMemo(() => createCollectionLeaveController(), []);
  const scopeKey = `collection.leave.${collectionId}.${userId}`;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const { requestClose, runAfterSave } = useUnsavedChangesGuard({
    scopeKey,
    open: true,
    dirty: false,
    pending: busy,
    onDiscard: () => {},
    onClose: () => onCancel?.(),
  });
  const dismiss = () => {
    void requestClose();
  };
  const leave = async () => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await controller.leave(collectionId, userId, role);
      controller.acknowledge();
      let completion = Promise.resolve();
      runAfterSave(() => {
        completion = Promise.resolve(onLeft());
      });
      await completion;
    } catch (leaveError) {
      setError(collectionLeaveErrorMessage(leaveError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Typography>
          Are you sure you want to leave the{' '}
          <strong>
            <bdi dir="auto">{collectionName}</bdi>
          </strong>{' '}
          collection? You’ll lose access to its items.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={dismiss}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={busy}
          onClick={() => {
            void leave();
          }}
        >
          {busy ? 'Leaving…' : 'Leave collection'}
        </Button>
      </DialogActions>
    </>
  );
}
